(function () {
  'use strict';

  const uniqueIdState = window.EightBpUniqueId;
  const STORAGE_KEY = uniqueIdState.storageKey;
  const RECENT_STORAGE_KEY = uniqueIdState.recentStorageKey;
  const SOURCE = 'eight-bp-blog-reward';

  function readConfig() {
    try {
      return JSON.parse(document.querySelector('[data-eight-bp-blog-id-config]')?.textContent || '{}');
    } catch (_) {
      return {};
    }
  }

  function normalize(value) {
    const id = String(value || '').trim();
    return /^\d+$/.test(id) ? id : '';
  }

  function readStoredId() {
    return uniqueIdState.read();
  }

  function readRecentIds() {
    return uniqueIdState.readRecent();
  }

  function storeVerifiedId(userId) {
    const id = normalize(userId);
    if (!id) return;
    uniqueIdState.set(id, { source: SOURCE, persist: true, remember: true });
  }

  class BlogUniqueIdEnhancer {
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.input = root.querySelector('#userId');
      this.claimButton = root.querySelector('#claimButton');
      this.inputContainer = this.input?.closest('.ebr-input-container');
      this.errorOverlay = root.querySelector('#errorOverlay');
      this.welcomeOverlay = root.querySelector('#welcomeOverlay');
      this.pendingId = '';
      if (!this.input || !this.claimButton || !this.inputContainer) return;

      this.onInput = this.onInput.bind(this);
      this.onInputFocus = this.onInputFocus.bind(this);
      this.onClaim = this.onClaim.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onRecentClick = this.onRecentClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onExternalId = this.onExternalId.bind(this);
      this.onStorage = this.onStorage.bind(this);
      this.onWelcomeChange = this.onWelcomeChange.bind(this);
      this.onWindowLoad = this.onWindowLoad.bind(this);
      this.enhance();
    }

    enhance() {
      if (this.root.dataset.eightBpBlogIdReady === 'true') return;
      this.root.dataset.eightBpBlogIdReady = 'true';
      this.input.inputMode = 'numeric';
      this.input.pattern = '[0-9]*';
      this.input.autocomplete = 'off';
      this.input.spellcheck = false;

      this.field = document.createElement('div');
      this.field.className = 'eight-bp-blog-id__field';
      this.input.before(this.field);
      this.field.appendChild(this.input);

      this.helpButton = document.createElement('button');
      this.helpButton.type = 'button';
      this.helpButton.className = 'eight-bp-blog-id__help';
      this.helpButton.textContent = '?';
      this.helpButton.setAttribute('aria-label', this.config.tooltip || '');
      this.helpButton.title = this.config.tooltip || '';
      this.field.appendChild(this.helpButton);

      this.recent = document.createElement('div');
      this.recent.className = 'eight-bp-blog-id__recent';
      this.recent.hidden = true;
      this.recent.innerHTML = `<span class="eight-bp-blog-id__recent-label"></span><div class="eight-bp-blog-id__recent-items"></div>`;
      this.recent.querySelector('.eight-bp-blog-id__recent-label').textContent = this.config.recentLabel || '';
      this.field.appendChild(this.recent);
      this.recentItems = this.recent.querySelector('.eight-bp-blog-id__recent-items');

      const storedId = readStoredId();
      if (storedId && !this.input.value.trim()) this.input.value = storedId;

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.onInputFocus);
      this.input.addEventListener('click', this.onInputFocus);
      this.input.addEventListener('keydown', this.onKeyDown, true);
      this.claimButton.addEventListener('click', this.onClaim, true);
      this.recent.addEventListener('click', this.onRecentClick);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('eight-bp:vip-id-changed', this.onExternalId);
      window.addEventListener('storage', this.onStorage);

      if (this.welcomeOverlay && 'MutationObserver' in window) {
        this.welcomeObserver = new MutationObserver(this.onWelcomeChange);
        this.welcomeObserver.observe(this.welcomeOverlay, { attributes: true, attributeFilter: ['class', 'style'] });
      }
      this.initializeTooltip();
    }

    initializeTooltip() {
      if (!this.config.tooltip || this.tooltip) return;
      if (typeof window.tippy !== 'function') {
        window.addEventListener('load', this.onWindowLoad, { once: true });
        return;
      }
      this.tooltip = window.tippy(this.helpButton, {
        content: this.config.tooltip,
        theme: document.body.dataset.tooltipColor,
        animation: document.body.dataset.tooltipAnimation,
        duration: [document.body.dataset.tooltipDuration || 300, 0],
        delay: document.body.dataset.tooltipDelay || 0,
        trigger: 'mouseenter focus click',
        touch: true,
        zIndex: 10001
      });
    }

    onWindowLoad() {
      this.initializeTooltip();
    }

    renderRecent(show = false) {
      const current = this.input.value.trim();
      const ids = readRecentIds().filter((id) => id !== current);
      const fragment = document.createDocumentFragment();
      ids.forEach((id) => {
        const item = document.createElement('span');
        item.className = 'eight-bp-blog-id__recent-item';

        const select = document.createElement('button');
        select.type = 'button';
        select.className = 'eight-bp-blog-id__recent-select';
        select.dataset.recentId = id;
        const value = document.createElement('strong');
        value.textContent = id;
        select.appendChild(value);

        const dismiss = document.createElement('button');
        dismiss.type = 'button';
        dismiss.className = 'eight-bp-blog-id__recent-dismiss';
        dismiss.dataset.dismissRecentId = id;
        dismiss.setAttribute('aria-label', this.config.dismissLabel || '');
        dismiss.textContent = '×';

        item.append(select, dismiss);
        fragment.appendChild(item);
      });
      this.recentItems.replaceChildren(fragment);
      this.recent.hidden = !show || ids.length === 0;
    }

    markInvalid(invalid) {
      if (invalid) this.input.setAttribute('aria-invalid', 'true');
      else this.input.removeAttribute('aria-invalid');
    }

    validate() {
      const id = normalize(this.input.value);
      this.markInvalid(!id);
      if (id) return id;
      this.showError(this.config.invalidUniqueId || 'Enter a valid numeric Unique ID.');
      this.input.focus();
      return '';
    }

    showError(message) {
      if (!this.errorOverlay) return;
      const status = this.errorOverlay.querySelector('.ebr-status-text');
      const action = this.errorOverlay.querySelector('.ebr-action-text');
      if (status) status.textContent = message;
      if (action) action.textContent = '';
      this.errorOverlay.style.display = 'flex';
      window.requestAnimationFrame(() => this.errorOverlay.classList.add('visible'));
    }

    onInput() {
      this.markInvalid(false);
      this.pendingId = '';
      this.renderRecent(document.activeElement === this.input);
    }

    onInputFocus() {
      this.renderRecent(true);
    }

    onKeyDown(event) {
      if (event.key !== 'Enter') return;
      const id = this.validate();
      if (id) this.pendingId = id;
      else {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    onClaim(event) {
      const id = this.validate();
      if (!id) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      this.pendingId = id;
      this.renderRecent(false);
    }

    onWelcomeChange() {
      if (!this.pendingId || !this.welcomeOverlay.classList.contains('visible')) return;
      storeVerifiedId(this.pendingId);
      this.pendingId = '';
    }

    onRecentClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      const select = target?.closest('[data-recent-id]');
      if (select) {
        this.input.value = select.dataset.recentId || '';
        this.markInvalid(false);
        this.renderRecent(false);
        this.input.focus();
        return;
      }

      const dismiss = target?.closest('[data-dismiss-recent-id]');
      if (!dismiss) return;
      const dismissedId = dismiss.dataset.dismissRecentId;
      uniqueIdState.forgetRecent(dismissedId);
      this.renderRecent(true);
      this.input.focus();
    }

    onDocumentClick(event) {
      if (!this.field.contains(event.target)) this.renderRecent(false);
    }

    onExternalId(event) {
      if (event.detail?.source === SOURCE) return;
      this.applyExternalId(event.detail?.userId || '');
    }

    onStorage(event) {
      if (event.key === STORAGE_KEY) this.applyExternalId(event.newValue || '');
      if (event.key === RECENT_STORAGE_KEY && document.activeElement === this.input) this.renderRecent(true);
    }

    applyExternalId(value) {
      const id = normalize(value);
      if (!id) {
        this.input.value = '';
        this.pendingId = '';
        this.markInvalid(false);
        this.renderRecent(false);
        return;
      }
      this.input.value = id;
      this.markInvalid(false);
      this.renderRecent(false);
    }
  }

  function initialize() {
    const root = document.querySelector('.eight-ball-rewards-post');
    if (!root || root.dataset.eightBpBlogIdReady === 'true') return;
    new BlogUniqueIdEnhancer(root, readConfig());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
